import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Building2, Store, Briefcase, MapPin, 
  TrendingUp, Clock, Target, Wrench, Users, DollarSign,
  ChevronRight, Filter, CheckCircle2, BarChart3, Home,
  ArrowUpRight, Calendar, Shield
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { trackRecord } from '../data/mock';

const CaseStudiesPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Slug mapping for case studies - use exact URLs
  const slugMapping = {
    'Jarmese Apartments (Anais Place)': 'jarmese-apartments',
    'Northgreen Atrium Building': 'northgreen-atrium',
    'Griggs Shopping Center': 'griggs-shopping-center',
    'Hollyvale Apartments': 'hollyvale-apartments'
  };

  // Featured case studies (from track record)
  const featuredCaseStudies = trackRecord.map(property => ({
    ...property,
    slug: slugMapping[property.name] || property.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, ''),
    assetClass: property.type.toLowerCase().includes('multifamily') ? 'multifamily' : 
                property.type.toLowerCase().includes('retail') ? 'retail' : 'commercial'
  }));

  // Filter case studies
  const filteredStudies = activeFilter === 'all' 
    ? featuredCaseStudies 
    : featuredCaseStudies.filter(study => study.assetClass === activeFilter);

  // Multifamily properties
  const multifamilyStudies = featuredCaseStudies.filter(s => s.assetClass === 'multifamily');
  
  // Commercial/Retail properties
  const commercialStudies = featuredCaseStudies.filter(s => s.assetClass !== 'multifamily');

  // Value-add strategies
  const valueAddStrategies = [
    { icon: Target, title: 'Operational Repositioning', description: 'Implement institutional-grade management practices' },
    { icon: Wrench, title: 'Targeted Renovations', description: 'Strategic capital improvements to drive rent growth' },
    { icon: Users, title: 'Leasing Optimization', description: 'Maximize occupancy through improved marketing' },
    { icon: DollarSign, title: 'Expense Management', description: 'Reduce operating costs without impacting quality' },
    { icon: TrendingUp, title: 'Capital Improvements', description: 'Align upgrades with market demand drivers' }
  ];

  // Track record highlights
  const trackRecordHighlights = [
    { label: 'Historical IRR Range', value: '20-28%', note: 'Realized investments' },
    { label: 'Equity Multiple Range', value: '1.8x-2.4x', note: 'Target returns' },
    { label: 'Typical Hold Period', value: '5-7 Years', note: 'Value-add cycle' },
    { label: 'Asset Classes', value: '3+', note: 'Diversified portfolio' }
  ];

  // Case Study Card Component
  const CaseStudyCard = ({ study, featured = false }) => {
    const statusColor = study.status === 'Realized' 
      ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
      : 'bg-blue-500/20 text-blue-400 border-blue-500/30';

    const typeIcon = study.assetClass === 'multifamily' ? Building2 : 
                     study.assetClass === 'retail' ? Store : Briefcase;
    const TypeIcon = typeIcon;

    return (
      <motion.div
        className={`group relative bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl border border-white/10 overflow-hidden hover:border-[#D4AF37]/30 transition-all duration-300 ${featured ? '' : ''}`}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        whileHover={{ y: -5 }}
      >
        {/* Image */}
        <div className={`relative overflow-hidden ${featured ? 'aspect-[16/10]' : 'aspect-[16/11]'}`}>
          <img 
            src={study.image} 
            alt={study.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/40 to-transparent" />
          
          {/* Status badge */}
          <div className="absolute top-4 left-4">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusColor}`}>
              {study.status}
            </span>
          </div>

          {/* Asset type badge */}
          <div className="absolute top-4 right-4">
            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white/80 text-xs border border-white/10">
              <TypeIcon className="h-3 w-3" />
              {study.type}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          <h3 className={`font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors ${featured ? 'text-xl' : 'text-lg'}`}>
            {study.name}
          </h3>
          
          <div className="flex items-center gap-2 text-white/50 text-sm mb-3">
            <MapPin className="h-3.5 w-3.5 text-[#D4AF37]" />
            <span>{study.location}</span>
          </div>

          <div className="flex items-center gap-4 text-white/40 text-xs mb-4">
            <span className="flex items-center gap-1">
              <Home className="h-3 w-3" />
              {study.units}
            </span>
            {study.irr !== '-' && (
              <span className="flex items-center gap-1 text-emerald-400">
                <TrendingUp className="h-3 w-3" />
                {study.irr} IRR
              </span>
            )}
            {study.multiple !== '-' && (
              <span className="flex items-center gap-1 text-[#D4AF37]">
                <BarChart3 className="h-3 w-3" />
                {study.multiple}
              </span>
            )}
          </div>

          <Link 
            to={`/case-studies/${study.slug}`}
            className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-medium group-hover:gap-3 transition-all"
          >
            View Case Study
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </motion.div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Real Estate Investment Case Studies | ASAP Capital</title>
        <meta 
          name="description" 
          content="Explore real estate investment case studies showcasing value-add multifamily, retail, and commercial properties across Houston and Texas." 
        />
        <meta name="keywords" content="real estate case studies, multifamily investment examples, Houston real estate investments, value-add property case studies, Texas commercial real estate" />
        <link rel="canonical" href="/case-studies" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-36 pb-16 lg:pt-44 lg:pb-20 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none" />
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
              >
                {/* Header */}
                <div className="max-w-3xl">
                  <motion.div 
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 bg-[#D4AF37]/10 border border-[#D4AF37]/20"
                    variants={itemVariants}
                  >
                    <Briefcase className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">Investment Examples</span>
                  </motion.div>

                  <motion.h1 
                    className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.2] mb-6"
                    variants={itemVariants}
                  >
                    Real Estate Investment{' '}
                    <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                      Case Studies
                    </span>
                  </motion.h1>

                  <motion.div 
                    className="space-y-4 text-white/60 text-base lg:text-lg leading-relaxed"
                    variants={itemVariants}
                  >
                    <p>
                      Our real estate investment case studies highlight how disciplined underwriting, 
                      operational execution, and value-add strategies translate into measurable 
                      property-level results. These examples provide transparency into how multifamily 
                      and commercial real estate investments are executed across Houston and key Texas markets.
                    </p>
                    <p>
                      Each case study reflects historical performance and illustrates the practical 
                      application of our investment process from acquisition through stabilization or exit.
                    </p>
                  </motion.div>

                  <motion.p 
                    className="mt-6 text-white/40 text-sm italic border-l-2 border-[#D4AF37]/30 pl-4"
                    variants={itemVariants}
                  >
                    Past performance is not indicative of future results. All investments involve risk.
                  </motion.p>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== FEATURED CASE STUDIES ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-8" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                    <TrendingUp className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Featured Case Studies
                  </h2>
                </motion.div>

                <div className="grid md:grid-cols-2 gap-6">
                  {featuredCaseStudies.map((study) => (
                    <CaseStudyCard key={study.id} study={study} featured={true} />
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== MULTIFAMILY CASE STUDIES ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center justify-between mb-8" variants={itemVariants}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                      <Building2 className="h-5 w-5 text-blue-400" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-white">
                      Multifamily Case Studies
                    </h2>
                  </div>
                  <span className="text-white/40 text-sm">{multifamilyStudies.length} Properties</span>
                </motion.div>

                {multifamilyStudies.length > 0 ? (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {multifamilyStudies.map((study) => (
                      <CaseStudyCard key={study.id} study={study} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-white/5 rounded-2xl border border-white/10">
                    <Building2 className="h-12 w-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">Additional multifamily case studies coming soon.</p>
                  </div>
                )}
              </motion.div>
            </div>
          </section>

          {/* ==================== COMMERCIAL & RETAIL CASE STUDIES ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center justify-between mb-8" variants={itemVariants}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                      <Store className="h-5 w-5 text-emerald-400" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-white">
                      Commercial & Retail Case Studies
                    </h2>
                  </div>
                  <span className="text-white/40 text-sm">{commercialStudies.length} Properties</span>
                </motion.div>

                {commercialStudies.length > 0 ? (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {commercialStudies.map((study) => (
                      <CaseStudyCard key={study.id} study={study} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-white/5 rounded-2xl border border-white/10">
                    <Store className="h-12 w-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">Additional commercial case studies coming soon.</p>
                  </div>
                )}
              </motion.div>
            </div>
          </section>

          {/* ==================== VALUE-ADD STRATEGY OVERVIEW ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-12 items-center"
              >
                {/* Left - Text content */}
                <motion.div variants={itemVariants}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/20">
                      <Target className="h-5 w-5 text-amber-400" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-white">
                      Value-Add Strategy Overview
                    </h2>
                  </div>

                  <p className="text-white/60 leading-relaxed mb-6">
                    Our case studies reflect consistent application of value-add investment strategies. 
                    These initiatives are designed to improve long-term asset fundamentals rather than 
                    rely on short-term market fluctuations.
                  </p>

                  <Link 
                    to="/strategy"
                    className="inline-flex items-center gap-2 text-[#D4AF37] font-medium hover:gap-3 transition-all"
                  >
                    View Our Investment Strategy
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </motion.div>

                {/* Right - Strategy steps */}
                <motion.div 
                  className="space-y-4"
                  variants={itemVariants}
                >
                  {valueAddStrategies.map((strategy, index) => (
                    <motion.div
                      key={strategy.title}
                      className="flex items-start gap-4 p-4 bg-gradient-to-r from-white/5 to-transparent rounded-xl border border-white/5 hover:border-[#D4AF37]/20 transition-all"
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0 border border-[#D4AF37]/20">
                        <strategy.icon className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold mb-1">{strategy.title}</h4>
                        <p className="text-white/50 text-sm">{strategy.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== TRACK RECORD HIGHLIGHTS ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="text-center mb-12" variants={itemVariants}>
                  <div className="inline-flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                      <BarChart3 className="h-5 w-5 text-purple-400" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-white">
                      Track Record Highlights
                    </h2>
                  </div>
                  <p className="text-white/50 max-w-2xl mx-auto">
                    Portfolio-level performance metrics demonstrating our disciplined approach 
                    to value-add real estate investing.
                  </p>
                </motion.div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  {trackRecordHighlights.map((metric, index) => (
                    <motion.div
                      key={metric.label}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10 text-center"
                      variants={itemVariants}
                      whileHover={{ y: -5, borderColor: 'rgba(212, 175, 55, 0.3)' }}
                    >
                      <p className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent mb-2">
                        {metric.value}
                      </p>
                      <p className="text-white font-medium mb-1">{metric.label}</p>
                      <p className="text-white/40 text-sm">{metric.note}</p>
                    </motion.div>
                  ))}
                </div>

                {/* Compliance note */}
                <motion.div 
                  className="bg-white/[0.02] border border-white/5 rounded-xl p-4 mb-8"
                  variants={itemVariants}
                >
                  <p className="text-white/40 text-sm text-center">
                    <Shield className="h-4 w-4 inline-block mr-2 text-white/30" />
                    Performance shown is historical and does not guarantee future results. 
                    All investments involve substantial risk including potential loss of principal.
                  </p>
                </motion.div>

                {/* CTA */}
                <motion.div className="text-center" variants={itemVariants}>
                  <Link to="/track-record">
                    <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-8 py-3 h-12 rounded-full text-sm shadow-lg shadow-[#D4AF37]/20">
                      View Full Track Record
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== LEARN MORE CTA ==================== */}
          <section className="py-20 lg:py-28 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 lg:p-12 border border-white/10 relative overflow-hidden"
              >
                {/* Background accents */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/5 rounded-full blur-3xl" />
                
                <div className="relative z-10 text-center max-w-2xl mx-auto">
                  <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                    Learn More About Our Investment Approach
                  </h2>
                  <p className="text-white/50 mb-8">
                    Discover how our disciplined process, operational expertise, and Houston market 
                    knowledge combine to create value for our investors.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/investment-process">
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-8 py-3 h-12 rounded-full text-sm shadow-lg shadow-[#D4AF37]/20 w-full sm:w-auto">
                          Review Investment Process
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </motion.div>
                    </Link>
                    <Link to="/contact">
                      <button className="cta-contact px-8 py-3 h-12 rounded-full text-sm w-full sm:w-auto">
                        Schedule Consultation
                        <Calendar className="h-4 w-4" />
                      </button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== RELATED PAGES ==================== */}
          <section className="py-12 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628] border-t border-white/5">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                <p className="text-white/40 text-sm mb-4">Related Resources</p>
                <div className="flex flex-wrap gap-3">
                  {[
                    { name: 'Portfolio', href: '/portfolio' },
                    { name: 'Track Record', href: '/track-record' },
                    { name: 'Investment Strategy', href: '/strategy' },
                    { name: 'Investment Process', href: '/investment-process' },
                    { name: 'Investor Resources', href: '/resources' }
                  ].map((link) => (
                    <Link 
                      key={link.name}
                      to={link.href}
                      className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm hover:border-[#D4AF37]/30 hover:text-[#D4AF37] transition-all"
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default CaseStudiesPage;
